import React, { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { useSubscriptions } from '../contexts/SubscriptionContext';
import { useAccounts } from '../contexts/AccountContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Plus, RefreshCw, MoreVertical, Edit2, Trash2, XCircle, PlayCircle, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';

const SERVICE_ICONS = {
  'Netflix': { color: '#E50914', letter: 'N' },
  'Spotify': { color: '#1DB954', letter: 'S' },
  'Amazon Prime': { color: '#FF9900', letter: 'A' },
  'YouTube': { color: '#FF0000', letter: 'Y' },
  'Disney+': { color: '#113CCF', letter: 'D' },
  'Apple': { color: '#555555', letter: 'A' },
  'Google': { color: '#4285F4', letter: 'G' },
  'Microsoft': { color: '#00A4EF', letter: 'M' },
  'default': { color: '#E8544F', letter: '?' }
};

const Subscriptions = () => {
  const { 
    subscriptions, loading, addSubscription, updateSubscription, 
    deleteSubscription, cancelSubscription, reactivateSubscription,
    getMonthlyTotal, getYearlyTotal 
  } = useSubscriptions();
  const { accounts } = useAccounts();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [deletingSub, setDeletingSub] = useState(null);
  const [cancellingSub, setCancellingSub] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [subForm, setSubForm] = useState({
    name: '',
    amount: '',
    billingCycle: 'monthly',
    startDate: '',
    account: '',
    category: '',
    notes: ''
  });

  const filteredSubs = useMemo(() => {
    return subscriptions.filter(sub => {
      if (activeFilter === 'all') return true;
      if (activeFilter === 'active') return sub.isActive;
      if (activeFilter === 'cancelled') return !sub.isActive;
      return true;
    });
  }, [subscriptions, activeFilter]);

  const getServiceIcon = (name) => {
    const key = Object.keys(SERVICE_ICONS).find(k => 
      name.toLowerCase().includes(k.toLowerCase())
    );
    return SERVICE_ICONS[key] || { color: SERVICE_ICONS.default.color, letter: name.charAt(0).toUpperCase() };
  };

  const handleOpenAddDialog = () => {
    setEditingSub(null);
    setSubForm({
      name: '',
      amount: '',
      billingCycle: 'monthly',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      account: '',
      category: '',
      notes: ''
    });
    setShowSubDialog(true);
  };

  const handleOpenEditDialog = (sub) => {
    setEditingSub(sub);
    setSubForm({
      name: sub.name,
      amount: sub.amount.toString(),
      billingCycle: sub.billingCycle,
      startDate: format(sub.startDate, 'yyyy-MM-dd'),
      account: sub.account || '',
      category: sub.category || '',
      notes: sub.notes || ''
    });
    setShowSubDialog(true);
  };

  const handleSaveSub = async () => {
    if (!subForm.name.trim()) {
      toast.error('Please enter a service name');
      return;
    }
    if (!subForm.amount || parseFloat(subForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!subForm.startDate) {
      toast.error('Please select a start date');
      return;
    }

    try {
      const subData = {
        name: subForm.name.trim(),
        amount: parseFloat(subForm.amount),
        billingCycle: subForm.billingCycle,
        startDate: subForm.startDate,
        account: subForm.account === 'none' ? null : (subForm.account || null),
        category: subForm.category || null,
        notes: subForm.notes || null
      };

      if (editingSub) {
        await updateSubscription(editingSub.id, subData);
        toast.success('Subscription updated!');
      } else {
        await addSubscription(subData);
        toast.success('Subscription added!');
      }

      setShowSubDialog(false);
      setEditingSub(null);
    } catch (error) {
      toast.error('Failed to save subscription');
    }
  };

  const handleDeleteSub = async () => {
    if (!deletingSub) return;
    try {
      await deleteSubscription(deletingSub.id);
      toast.success('Subscription deleted');
      setShowDeleteDialog(false);
      setDeletingSub(null);
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  const handleCancelSub = async () => {
    if (!cancellingSub) return;
    try {
      await cancelSubscription(cancellingSub.id);
      toast.success('Subscription cancelled');
      setShowCancelDialog(false);
      setCancellingSub(null);
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleReactivate = async (sub) => {
    try {
      await reactivateSubscription(sub.id);
      toast.success('Subscription reactivated!');
    } catch (error) {
      toast.error('Failed to reactivate subscription');
    }
  };

  const getCycleLabel = (cycle) => {
    return { monthly: '/month', yearly: '/year', weekly: '/week' }[cycle] || '';
  };

  const getDaysUntilRenewal = (renewalDate) => {
    const days = differenceInDays(renewalDate, new Date());
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 0) return 'Past due';
    return `${days} days`;
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Subscriptions</h1>
          <Button
            onClick={handleOpenAddDialog}
            className="flex items-center gap-2"
            style={{ backgroundColor: theme.accent, color: 'white' }}
            data-testid="add-subscription-btn"
          >
            <Plus className="w-4 h-4" />
            Add
          </Button>
        </div>

        {/* Summary Card */}
        <div 
          className="rounded-xl p-4 mb-6 grid grid-cols-2 gap-4"
          style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
        >
          <div>
            <p className="text-sm" style={{ color: theme.textSecondary }}>Monthly Total</p>
            <p className="text-2xl font-bold" style={{ color: theme.accent }}>
              {formatCurrency(getMonthlyTotal())}
            </p>
          </div>
          <div>
            <p className="text-sm" style={{ color: theme.textSecondary }}>Yearly Cost</p>
            <p className="text-2xl font-bold" style={{ color: theme.text }}>
              {formatCurrency(getYearlyTotal())}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['all', 'active', 'cancelled'].map(filter => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className="px-4 py-2 rounded-lg font-medium text-sm transition-all"
              style={{
                backgroundColor: activeFilter === filter 
                  ? theme.accent 
                  : (isDark ? '#2C2C2E' : '#F8F9FA'),
                color: activeFilter === filter ? 'white' : theme.textSecondary
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        {/* Subscriptions List */}
        {loading ? (
          <div className="text-center py-12">
            <p style={{ color: theme.textSecondary }}>Loading subscriptions...</p>
          </div>
        ) : filteredSubs.length === 0 ? (
          <div 
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
          >
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: isDark ? '#2C2C2E' : '#F8F9FA' }}
            >
              <RefreshCw className="w-8 h-8" style={{ color: theme.accent }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
              {activeFilter === 'all' ? 'No subscriptions yet' : `No ${activeFilter} subscriptions`}
            </h3>
            <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
              {activeFilter === 'all' ? 'Tap + to add your first subscription' : 'Check other filters'}
            </p>
            {activeFilter === 'all' && (
              <Button onClick={handleOpenAddDialog} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Add Subscription
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSubs.map(sub => {
              const icon = getServiceIcon(sub.name);

              return (
                <div
                  key={sub.id}
                  className={`rounded-xl p-4 transition-all hover:shadow-lg ${!sub.isActive ? 'opacity-60' : ''}`}
                  style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
                  data-testid={`subscription-card-${sub.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: icon.color }}
                      >
                        {icon.letter}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 
                            className={`font-semibold ${!sub.isActive ? 'line-through' : ''}`} 
                            style={{ color: theme.text }}
                          >
                            {sub.name}
                          </h3>
                          {sub.isActive ? (
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                              Cancelled
                            </span>
                          )}
                        </div>
                        <p className="text-sm" style={{ color: theme.textSecondary }}>
                          {sub.isActive 
                            ? `Renews: ${format(sub.nextRenewalDate, 'MMM d, yyyy')} (${getDaysUntilRenewal(sub.nextRenewalDate)})`
                            : 'No longer active'
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-lg" style={{ color: theme.text }}>
                          {formatCurrency(sub.amount)}
                        </p>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>
                          {getCycleLabel(sub.billingCycle)}
                        </p>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-gray-500">
                            <MoreVertical className="w-5 h-5" style={{ color: theme.textSecondary }} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent style={{ backgroundColor: theme.cardBackground }}>
                          <DropdownMenuItem onClick={() => handleOpenEditDialog(sub)} style={{ color: theme.text }}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {sub.isActive ? (
                            <DropdownMenuItem 
                              onClick={() => { setCancellingSub(sub); setShowCancelDialog(true); }}
                              style={{ color: '#D97706' }}
                            >
                              <XCircle className="w-4 h-4 mr-2" /> Cancel Subscription
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleReactivate(sub)}
                              style={{ color: '#4CAF50' }}
                            >
                              <PlayCircle className="w-4 h-4 mr-2" /> Reactivate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => { setDeletingSub(sub); setShowDeleteDialog(true); }}
                            style={{ color: theme.accent }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Account Info */}
                  {sub.account && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2" style={{ borderColor: theme.divider }}>
                      <CreditCard className="w-4 h-4" style={{ color: theme.textSecondary }} />
                      <span className="text-sm" style={{ color: theme.textSecondary }}>
                        Charges: {sub.account}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>
                {editingSub ? 'Edit Subscription' : 'Add Subscription'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label style={{ color: theme.textSecondary }}>Service Name</Label>
                <Input
                  value={subForm.name}
                  onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                  placeholder="e.g., Netflix, Spotify"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Amount ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  value={subForm.amount}
                  onChange={(e) => setSubForm({ ...subForm, amount: e.target.value })}
                  placeholder="149"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Billing Cycle</Label>
                <Select value={subForm.billingCycle} onValueChange={(v) => setSubForm({ ...subForm, billingCycle: v })}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Start Date</Label>
                <Input
                  type="date"
                  value={subForm.startDate}
                  onChange={(e) => setSubForm({ ...subForm, startDate: e.target.value })}
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Account (Optional)</Label>
                <Select value={subForm.account} onValueChange={(v) => setSubForm({ ...subForm, account: v })}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.name}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Notes (Optional)</Label>
                <Input
                  value={subForm.notes}
                  onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })}
                  placeholder="Any notes..."
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubDialog(false)} style={{ color: theme.text }}>
                Cancel
              </Button>
              <Button onClick={handleSaveSub} style={{ backgroundColor: theme.accent, color: 'white' }}>
                {editingSub ? 'Update' : 'Add'} Subscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Confirmation */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Cancel Subscription</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to cancel "{cancellingSub?.name}"? This will mark it as cancelled and stop tracking renewals.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Active</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelSub} style={{ backgroundColor: '#D97706' }}>
                Cancel Subscription
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Delete Subscription</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to delete "{deletingSub?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteSub} style={{ backgroundColor: '#DC3545' }}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Subscriptions;
